import {
  SectionElement,
  defineElement,
  on,
  onClick,
  query,
  queryAll
} from "./chunk-DNDL4K7L.js";
import {
  __async,
  __decorateClass
} from "./chunk-XDKWOSVF.js";

// src/shared/worldwide-addresses.ts
var addressFormats = {
  US: {
    order: ["streetAndNumber", "city", "state", "zip"],
    required: ["streetAndNumber", "city", "state", "zip"],
    labels: {
      streetAndNumber: "data-label-street-address",
      city: "data-label-city",
      state: "data-label-state",
      zip: "data-label-postal-code"
    },
    example: {
      streetAndNumber: "123 Main Street",
      city: "New York",
      state: "NY",
      zip: "10001"
    },
    selects: {
      state: [
        "AL",
        "AK",
        "AZ",
        "AR",
        "CA",
        "CO",
        "CT",
        "DE",
        "FL",
        "GA",
        "HI",
        "ID",
        "IL",
        "IN",
        "IA",
        "KS",
        "KY",
        "LA",
        "ME",
        "MD",
        "MA",
        "MI",
        "MN",
        "MS",
        "MO",
        "MT",
        "NE",
        "NV",
        "NH",
        "NJ",
        "NM",
        "NY",
        "NC",
        "ND",
        "OH",
        "OK",
        "OR",
        "PA",
        "RI",
        "SC",
        "SD",
        "TN",
        "TX",
        "UT",
        "VT",
        "VA",
        "WA",
        "WV",
        "WI",
        "WY"
      ]
    },
    layout: [
      { field: "streetAndNumber", width: "half" },
      { field: "city", width: "half" },
      { field: "state", width: "half" },
      { field: "zip", width: "half" }
    ]
  },
  CA: {
    order: ["streetAndNumber", "city", "state", "zip"],
    required: ["streetAndNumber", "city", "state", "zip"],
    labels: {
      streetAndNumber: "data-label-street-address",
      city: "data-label-city",
      state: "data-label-province",
      zip: "data-label-postal-code"
    },
    example: {
      streetAndNumber: "123 Main Street",
      city: "Toronto",
      state: "ON",
      zip: "M5V 3A8"
    },
    selects: {
      state: [
        "AB",
        "BC",
        "MB",
        "NB",
        "NL",
        "NS",
        "NT",
        "NU",
        "ON",
        "PE",
        "QC",
        "SK",
        "YT"
      ]
    },
    layout: [
      { field: "streetAndNumber", width: "half" },
      { field: "city", width: "half" },
      { field: "state", width: "half" },
      { field: "zip", width: "half" }
    ]
  },
  UK: {
    order: ["streetAndNumber", "city", "zip"],
    required: ["streetAndNumber", "city", "zip"],
    labels: {
      streetAndNumber: "data-label-street-address",
      city: "data-label-city",
      zip: "data-label-postal-code"
    },
    example: {
      streetAndNumber: "10 Downing Street",
      city: "London",
      zip: "SW1A 2AA"
    },
    selects: {
      country: ["England", "Scotland", "Wales", "Northern Ireland"]
    },
    layout: [
      { field: "streetAndNumber", width: "half" },
      { field: "city", width: "half" },
      { field: "zip", width: "half" }
    ]
  },
  DE: {
    order: ["streetAndNumber", "zip", "city", "state"],
    required: ["streetAndNumber", "zip", "city"],
    labels: {
      streetAndNumber: "data-label-street-address",
      zip: "data-label-postal-code",
      city: "data-label-city",
      state: "data-label-state"
    },
    example: {
      streetAndNumber: "Unter den Linden 77",
      zip: "10117",
      city: "Berlin",
      state: "Berlin"
    },
    selects: {
      state: [
        "Baden-W\xFCrttemberg",
        "Bayern",
        "Berlin",
        "Brandenburg",
        "Bremen",
        "Hamburg",
        "Hessen",
        "Mecklenburg-Vorpommern",
        "Niedersachsen",
        "Nordrhein-Westfalen",
        "Rheinland-Pfalz",
        "Saarland",
        "Sachsen",
        "Sachsen-Anhalt",
        "Schleswig-Holstein",
        "Th\xFCringen"
      ]
    },
    layout: [
      { field: "streetAndNumber", width: "half" },
      { field: "zip", width: "half" },
      { field: "city", width: "half" },
      { field: "state", width: "half" }
    ]
  }
  // Add more countries as needed...
};
var europeanCountries = ["FR", "ES", "IT", "NL", "BE", "SE", "NO", "DK", "FI", "PT", "GR", "CH", "AT", "PL", "CZ", "IE"];
europeanCountries.forEach((country) => {
  if (!addressFormats[country]) {
    addressFormats[country] = {
      order: ["streetAndNumber", "zip", "city"],
      required: ["streetAndNumber", "zip", "city"],
      labels: {
        streetAndNumber: "data-label-street-address",
        zip: "data-label-postal-code",
        city: "data-label-city"
      },
      example: {
        streetAndNumber: "Rue de la Paix 1",
        zip: "75001",
        city: "Paris"
      },
      layout: [
        { field: "streetAndNumber", width: "half" },
        { field: "zip", width: "half" },
        { field: "city", width: "half" }
      ]
    };
  }
});
function getFormat(countryCode) {
  return addressFormats[countryCode] || null;
}
function getAddressFields(countryCode) {
  const format = getFormat(countryCode);
  if (!format)
    return null;
  return format.layout.map((layoutItem) => {
    var _a;
    const field = layoutItem.field;
    return {
      field,
      label: format.labels[field],
      required: format.required.includes(field),
      width: layoutItem.width,
      selectOptions: (_a = format.selects) == null ? void 0 : _a[field],
      example: format.example[field] != null ? format.example[field] : ""
    };
  });
}

// src/sections/shop-submission/shop-submission.ts
var ShopSubmission = class extends SectionElement {
  constructor() {
    super(...arguments);
    this.recaptchaLoaded = false;
    this.regionNames = new Intl.DisplayNames(
      ["en"],
      { type: "region" }
    );
  }
  // Helper function to get localized text from data attributes
  getLocalizedText(key) {
    const translationsDiv = this.querySelector("[data-translations]");
    const text = translationsDiv == null ? void 0 : translationsDiv.getAttribute(key);
    return text != null ? text : key;
  }
  onConsentCheckboxChange(checkbox) {
    this.consentCheckboxes.forEach((cb) => {
      if (cb !== checkbox) {
        cb.checked = checkbox.checked;
      }
    });
    if (checkbox.checked && !this.recaptchaLoaded) {
      this.loadRecaptchaScript();
    }
  }
  loadRecaptchaScript() {
    return __async(this, null, function* () {
      if (this.recaptchaLoaded)
        return;
      return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = "https://www.google.com/recaptcha/api.js?render=6LeEl08qAAAAABhP6ACT65yJS2CZ-WJLm1YB55fM";
        script.async = true;
        script.defer = true;
        script.onload = () => {
          this.recaptchaLoaded = true;
          resolve();
        };
        script.onerror = () => reject(new Error("Failed to load reCAPTCHA script"));
        document.head.appendChild(script);
      });
    });
  }
  connectedCallback() {
    const customAddress = this.addressCustomElement.dataset.customAddress;
    this.render(customAddress != null ? customAddress : "DE");
    this.countrySelectElement.addEventListener("country-changed", (event) => {
      this.render(event.detail);
    });
    this.initializeTable();
  }
  initializeTable() {
    this.createNewRow();
    this.addRowBtn.addEventListener("click", () => {
      this.createNewRow();
    });
  }
  createNewRow() {
    const row = document.createElement("tr");
    const newRowNum = this.tableBody.children.length + 1;
    row.className = "flex items-center gap-2";
    row.setAttribute("role", "row");
    const labels = {
      partNumber: this.getLocalizedText("data-label-part-number"),
      minQuantity: this.getLocalizedText("data-label-min-quantity"),
      maxQuantity: this.getLocalizedText("data-label-max-quantity"),
      targetPrice: this.getLocalizedText("data-label-target-price"),
      deleteRow: this.getLocalizedText("data-label-delete-row")
    };
    row.innerHTML = `
      <td class="flex-1" headers="part-number-header">
        <input type="text" 
              name="part_number_${newRowNum}"
              aria-label="${labels.partNumber}"
              class="part-number w-full p-1.5 border border-gray-300 rounded focus:ring-primary focus:border-primary">
      </td>
      <td class="flex-1" headers="min-quantity-header">
        <input type="number" 
              name="min_qty_${newRowNum}"
              aria-label="${labels.minQuantity}"
              min="0"
              class="min-qty w-full p-1.5 border border-gray-300 rounded focus:ring-primary focus:border-primary">
      </td>
      <td class="flex-1" headers="max-quantity-header">
        <input type="number" 
              name="max_qty_${newRowNum}"
              aria-label="${labels.maxQuantity}"
              min="0"
              class="max-qty w-full p-1.5 border border-gray-300 rounded focus:ring-primary focus:border-primary">
      </td>
      <td class="flex-1" headers="target-price-header">
        <input type="number" 
              name="target_price_${newRowNum}"
              aria-label="${labels.targetPrice}"
              min="0"
              step="0.01"
              class="target-price w-full p-1.5 border border-gray-300 rounded focus:ring-primary focus:border-primary">
      </td>
      <td class="flex items-center justify-center w-[40px]" headers="actions-header">
        <button type="button" 
                class="delete-btn px-1 py-1 transition-colors"
                data-delete-row
                aria-label="${labels.deleteRow}">
          <util-icon icon="cross" class="h-4 w-4"></util-icon>
        </button>
      </td>
    `;
    this.tableBody.appendChild(row);
    this.updateRowNumbers();
  }
  deleteRow(event) {
    const button = event.target;
    const row = button.closest("tr");
    if (row && this.tableBody.children.length > 1) {
      row.remove();
      this.updateRowNumbers();
    } else {
      const cannotDeleteMsg = this.getLocalizedText("data-label-cannot-delete-last-row");
      alert(cannotDeleteMsg);
    }
  }
  updateRowNumbers() {
    Array.from(this.tableBody.children).forEach((row, index) => {
      const rowNum = index + 1;
      const tr = row;
      const partNumberInput = tr.querySelector(".part-number");
      const minQtyInput = tr.querySelector(".min-qty");
      const maxQtyInput = tr.querySelector(".max-qty");
      const targetPriceInput = tr.querySelector(".target-price");
      const deleteBtn = tr.querySelector(".delete-btn");
      if (partNumberInput)
        partNumberInput.name = `part_number_${rowNum}`;
      if (minQtyInput)
        minQtyInput.name = `min_qty_${rowNum}`;
      if (maxQtyInput)
        maxQtyInput.name = `max_qty_${rowNum}`;
      if (targetPriceInput)
        targetPriceInput.name = `target_price_${rowNum}`;
      if (deleteBtn) {
        deleteBtn.disabled = this.tableBody.children.length === 1;
        if (deleteBtn.disabled) {
          deleteBtn.className = "delete-btn px-1 py-1 transition-colors opacity-50 cursor-not-allowed";
          const cannotDeleteMsg = this.getLocalizedText("data-label-cannot-delete-last-row");
          deleteBtn.setAttribute("aria-label", cannotDeleteMsg);
        } else {
          deleteBtn.className = "delete-btn px-1 py-1 transition-colors hover:opacity-75";
          const deleteMsg = this.getLocalizedText("data-label-delete-row");
          deleteBtn.setAttribute("aria-label", deleteMsg);
        }
      }
    });
  }
  getProductIds() {
    const productElements = this.querySelectorAll("[data-product-id]");
    return Array.from(productElements).map((el) => {
      var _a;
      return (_a = el.dataset.productId) != null ? _a : "";
    });
  }
  getProductData() {
    return Array.from(this.tableBody.children).map((row) => {
      var _a, _b, _c, _d, _e, _f, _g, _h;
      const tr = row;
      return {
        articleNumber: (_b = (_a = tr.querySelector(".part-number")) == null ? void 0 : _a.value) != null ? _b : "",
        minimumQuantity: (_d = (_c = tr.querySelector(".min-qty")) == null ? void 0 : _c.value) != null ? _d : "",
        maximumQuantity: (_f = (_e = tr.querySelector(".max-qty")) == null ? void 0 : _e.value) != null ? _f : "",
        targetPrice: (_h = (_g = tr.querySelector(".target-price")) == null ? void 0 : _g.value) != null ? _h : ""
      };
    }).filter(
      (data) => data.articleNumber || data.minimumQuantity || data.maximumQuantity || data.targetPrice
    );
  }
  getCountryName() {
    var _a, _b;
    const countrySelect = this.form.querySelector('[name="country"]');
    return (_b = (_a = countrySelect == null ? void 0 : countrySelect.selectedOptions[0]) == null ? void 0 : _a.text) != null ? _b : "";
  }
  collectFormData(captchaToken) {
    const formData = new FormData(this.form);
    return {
      lang: this.dataset.lang || "en",
      captchaToken,
      productIds: this.getProductIds(),
      name: formData.get("first_name"),
      lastName: formData.get("last_name"),
      phone: formData.get("phone"),
      email: formData.get("email"),
      company: formData.get("organization") || "",
      jobDescription: formData.get("jobDescription"),
      country: this.getCountryName(),
      streetAndNumber: formData.get("streetAndNumber"),
      zip: formData.get("zip"),
      city: formData.get("city"),
      state: formData.get("state") || void 0,
      productData: this.getProductData(),
      additionalInformation: formData.get("optionalData") || ""
    };
  }
  handleSendApplication() {
    return __async(this, null, function* () {
      var _a;
      this.submitButton.forEach((element) => {
        element.setAttribute("loading", "");
      });
      this.errorElement.classList.add("hidden");
      const isConsentGiven = this.consentCheckboxes.some((cb) => cb.checked);
      if (!isConsentGiven) {
        this.submitButton.forEach((element) => {
          element.removeAttribute("loading");
        });
        return;
      }
      if (this.form.checkValidity()) {
        try {
          if (!this.recaptchaLoaded) {
            yield this.loadRecaptchaScript();
          }
          const captchaToken = yield this.executeRecaptcha();
          const result = yield this.sendRequest(captchaToken);
          if (result.success) {
            window.location.href = "/pages/thank-you";
          } else {
            this.showError((_a = result.error) != null ? _a : "An error occurred");
          }
        } catch (error) {
          if (error instanceof Error) {
            if (error.name === "AbortError") {
              this.showError("Request timed out. Please try again.");
            } else {
              this.showError(`An error occurred: ${error.message}`);
            }
            console.error("Error:", error);
          } else {
            this.showError("An unknown error occurred");
            console.error("Unknown error:", error);
          }
        }
      } else {
        this.showError("Please fill out all required fields correctly.");
        this.form.reportValidity();
      }
      this.submitButton.forEach((element) => {
        element.removeAttribute("loading");
      });
    });
  }
  showError(message) {
    this.errorElement.classList.remove("hidden");
    this.errorMessageElement.textContent = message;
  }
  executeRecaptcha() {
    return __async(this, null, function* () {
      if (!window.grecaptcha) {
        throw new Error("reCAPTCHA not loaded.");
      }
      return yield new Promise((resolve, reject) => {
        window.grecaptcha.ready(() => {
          window.grecaptcha.execute("6LeEl08qAAAAABhP6ACT65yJS2CZ-WJLm1YB55fM", { action: "submit" }).then(resolve).catch(reject);
        });
      });
    });
  }
  sendRequest(captchaToken) {
    return __async(this, null, function* () {
      var _a;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 3e4);
      try {
        const requestData = this.collectFormData(captchaToken);
        const res = yield fetch("/apps/bb-tools/submitCart", {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(requestData),
          signal: controller.signal
        });
        clearTimeout(timeoutId);
        const data = yield res.json();
        if (res.ok) {
          return data;
        } else {
          return { success: false, error: (_a = data.error) != null ? _a : "Server error" };
        }
      } catch (error) {
        clearTimeout(timeoutId);
        throw error;
      }
    });
  }
  render(countryCode) {
    const addressFields = countryCode ? getAddressFields(countryCode) : getAddressFields("DE");
    if (addressFields) {
      document.querySelectorAll(".custom_generated").forEach((el) => {
        el.remove();
      });
      const customdiv = document.createElement("div");
      customdiv.dataset.customAddressFields = "";
      let currentElement = this.addressFieldsElement.nextElementSibling;
      while (currentElement) {
        const nextElement = currentElement.nextElementSibling;
        currentElement.remove();
        currentElement = nextElement;
      }
      customdiv.innerHTML = addressFields.map((field) => this.renderInputField(field)).join("");
      this.addressCustomElement.insertAdjacentHTML("beforeend", customdiv.innerHTML);
    } else {
      console.error("Failed to get address fields");
    }
  }
  renderInputField(fieldInfo) {
    const commonClasses = "block w-full rounded-md border-gray-200  focus:border-primary focus:ring-primary sm:text-base !leading-none px-4 py-3 text-gray-700";
    const widthClass = fieldInfo.width === "half" ? "col-span-3 sm:col-span-2" : "col-span-3 sm:col-span-4";
    const label = this.getLocalizedText(fieldInfo.label);
    const placeholder = fieldInfo.example;
    const selectText = this.getLocalizedText("data-label-select");
    if (fieldInfo.selectOptions) {
      return `
        <div class="${widthClass} custom_generated">
          <label for="ShopSubmission--${fieldInfo.field}" class="block text-xs font-semibold text-gray-700">
            ${label}${fieldInfo.required ? '<span class="text-primary">*</span>' : ""}
          </label>
          <div class="mt-2.5">
            <select 
              id="ShopSubmission--${fieldInfo.field}" 
              name="${fieldInfo.field}" 
              class="${commonClasses} w-full h-[46px]"
              ${fieldInfo.required ? "required" : ""}
            >
              <option value="">${selectText} ${label}</option>
              ${fieldInfo.selectOptions.map((option) => `<option value="${option}">${option}</option>`).join("")}
            </select>
          </div>
        </div>
      `;
    } else {
      return `
        <div class="${widthClass}">
          <label for="ShopSubmission--${fieldInfo.field}" class="block text-xs font-semibold text-gray-700">
            ${label}${fieldInfo.required ? '<span class="text-primary">*</span>' : ""}
          </label>
          <div class="mt-2.5">
            <input 
              type="text" 
              id="ShopSubmission--${fieldInfo.field}" 
              name="${fieldInfo.field}" 
              placeholder="${placeholder}"
              class="${commonClasses}"
              ${fieldInfo.required ? "required" : ""}
            />
          </div>
        </div>
      `;
    }
  }
};
__decorateClass([
  query("[data-custom-form]")
], ShopSubmission.prototype, "form", 2);
__decorateClass([
  query("[data-custom-address]")
], ShopSubmission.prototype, "addressCustomElement", 2);
__decorateClass([
  query("[data-custom-address-fields]")
], ShopSubmission.prototype, "addressFieldsElement", 2);
__decorateClass([
  queryAll("[data-offer-submit]")
], ShopSubmission.prototype, "submitButton", 2);
__decorateClass([
  queryAll("[data-consent-checkbox]")
], ShopSubmission.prototype, "consentCheckboxes", 2);
__decorateClass([
  query("country-select")
], ShopSubmission.prototype, "countrySelectElement", 2);
__decorateClass([
  query("[data-error]")
], ShopSubmission.prototype, "errorElement", 2);
__decorateClass([
  query("[data-error-msg]")
], ShopSubmission.prototype, "errorMessageElement", 2);
__decorateClass([
  query("tbody")
], ShopSubmission.prototype, "tableBody", 2);
__decorateClass([
  query(".add-row-btn")
], ShopSubmission.prototype, "addRowBtn", 2);
__decorateClass([
  on("change", "[data-consent-checkbox]")
], ShopSubmission.prototype, "onConsentCheckboxChange", 1);
__decorateClass([
  onClick("[data-delete-row]")
], ShopSubmission.prototype, "deleteRow", 1);
__decorateClass([
  onClick("[data-offer-submit]")
], ShopSubmission.prototype, "handleSendApplication", 1);
ShopSubmission = __decorateClass([
  defineElement("shop-submission")
], ShopSubmission);
var CountrySelect = class extends HTMLElement {
  constructor() {
    var _a, _b;
    super();
    this.handleChange = (event) => {
      this.selectedCode = event.target.value;
      const addressCustomElement = document.querySelector("[data-custom-address]");
      if (addressCustomElement != null) {
        addressCustomElement.dataset.customAddress = this.selectedCode;
      }
      this.render();
      this.dispatchEvent(new CustomEvent("country-changed", { detail: this.selectedCode }));
    };
    this.regions = ["US", "CA", "UK", "DE", "FR", "ES", "IT", "NL", "BE", "SE", "NO", "DK", "FI", "PT", "GR", "CH", "AT", "PL", "CZ", "IE"];
    this.regionNames = new Intl.DisplayNames(["en"], { type: "region" });
    this.selectedCode = (_b = (_a = document.querySelector("[data-custom-address]")) == null ? void 0 : _a.dataset.customAddress) != null ? _b : "";
  }
  // Helper function to get localized text from data attributes
  getLocalizedText(key) {
    const shopSubmission = document.querySelector("shop-submission");
    const translationsDiv = shopSubmission == null ? void 0 : shopSubmission.querySelector("[data-translations]");
    const text = translationsDiv == null ? void 0 : translationsDiv.getAttribute(key);
    return text != null ? text : key;
  }
  connectedCallback() {
    this.render();
    this.addEventListener("change", this.handleChange);
  }
  render() {
    const countryLabel = this.getLocalizedText("data-label-country");
    const selectCountryText = this.getLocalizedText("data-label-select-country");
    this.innerHTML = `
      <label for="ShopSubmission--country" class="block text-xs font-semibold text-gray-700">
        ${countryLabel}<span class="text-primary">*</span>
      </label>
      <div class="mt-2.5">
        <select required 
          id="ShopSubmission--country" 
          name="country" 
          class="block w-full rounded-md border-gray-200 focus:border-primary focus:ring-primary sm:text-base !leading-none px-4 py-3 text-gray-700 h-[46px]"
        >
          <option>${selectCountryText}</option>
          ${this.regions.map((region) => {
      var _a;
      return `
            <option value="${region}" ${this.selectedCode === region ? "selected" : ""}>
              ${(_a = this.regionNames.of(region)) != null ? _a : "Unknown Region"}
            </option>
          `;
    }).join("")}
        </select>
      </div>
    `;
  }
};
customElements.define("country-select", CountrySelect);
export {
  ShopSubmission
};
